/* eslint-disable promise/prefer-await-to-callbacks, sonarjs/cognitive-complexity */
import {
  faShoppingCart,
  faEnvelope,
  faUser,
  faCog,
} from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import React, { useContext, useState, useEffect } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import AccordionContext from 'react-bootstrap/AccordionContext';
import { useAccordionToggle } from 'react-bootstrap/AccordionToggle';
import { useDispatch, useSelector } from 'react-redux';
import { MOBILE_MENU } from '../../../brands/default/content/components/header';
import { RAF } from '../../../brands/default/features';
import {
  LOGIN_MENU,
  MOBILE_MENU_MORE_LINKS,
  RAF_ENABLED_LOGIN_MENU,
  REGISTER_MENU,
} from '../../../brands/default/links';
import { setPostLoginUrl } from '../../../helpers/cookieSetter';
import { isSSR } from '../../../helpers/ssr';
import {
  useNavigationSelection,
  findLinkMatchingPath,
} from '../../../helpers/url';
import ThemeContext from '../../../providers/ThemeProvider';
import { setFooterValue } from '../../../redux/actions/footer';
import SubscribeModal from '../../forms/SubscribeModalDynamic';
import { HeaderLogout } from '../HeaderLogout';
import MobileMenuSectionHead from './MobileMenuSectionHead';
import MobileNavigationLink from './MobileNavigationLink';

const ContextAwareToggle = ({ eventKey, callback, faIcon, buttonText }) => {
  const currentEventKey = useContext(AccordionContext);
  const decoratedOnClick = useAccordionToggle(
    eventKey,
    () => callback && callback(eventKey),
  );
  const isCurrentEventKey = currentEventKey === eventKey;

  return (
    <>
      <button
        className="head-section__button"
        onClick={decoratedOnClick}
        type="button"
      >
        <MobileMenuSectionHead
          buttonText={buttonText}
          faIcon={faIcon}
          isCurrentEventKey={isCurrentEventKey}
          isExpander
        />
      </button>
      <style jsx>{`
        .head-section__button {
          background-color: white;
          border: 0;
          text-align: left;
          padding: 0;
          width: 100%;
        }
      `}</style>
    </>
  );
};

ContextAwareToggle.propTypes = {
  buttonText: PropTypes.string,
  callback: PropTypes.func,
  eventKey: PropTypes.string,
  faIcon: PropTypes.object,
};

const MobileMenu = ({ isFindPage = false, navigation, openMenu, path }) => {
  const theme = useContext(ThemeContext);
  const navSelection = useNavigationSelection(navigation, path);

  const moreLinks = MOBILE_MENU_MORE_LINKS;
  const moreSelection =
    moreLinks && path ? findLinkMatchingPath(moreLinks, path) : '';
  const moreSelectionHref = moreSelection ? moreSelection.href : null;
  const defaultAccordionKey = moreSelection ? 'MORE_KEY' : 'NAV_KEY';

  const userState = useSelector((state) => state.user.isAuthenticated);
  const locations = useSelector((state) => state.locations.locations);

  const rafEnabled = RAF;
  const loggedInLinks = rafEnabled ? RAF_ENABLED_LOGIN_MENU : LOGIN_MENU;
  const loggedOutLinks = REGISTER_MENU;
  const userMenu = userState ? loggedInLinks : loggedOutLinks;
  const [showSubModal, setShowSubModal] = useState(false);
  const [currentNav, setCurrentNav] = useState([navigation]);
  const isWindowDefined = !isSSR();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isWindowDefined) {
      window.closeSubModal = function () {
        setShowSubModal(false);
      };
    }
  }, [isWindowDefined]);

  const openSubMenu = (subNavigation) => {
    setCurrentNav([...currentNav, subNavigation]);
  };
  const handleShowSubModal = (value) => {
    setShowSubModal(value);
  };

  const goBack = () => {
    const updatedNav = [...currentNav];
    updatedNav.pop();
    setCurrentNav(updatedNav);
  };

  const currentNavItems = currentNav[currentNav.length - 1];
  const setCookie = () => {
    setPostLoginUrl(path);
  };

  const showFooter = () => {
    openMenu(false);
    dispatch(setFooterValue(true));
  };

  return (
    <>
      <div className="mobile-menu">
        <Accordion defaultActiveKey={defaultAccordionKey}>
          {currentNav.length > 1 && (
            <button className="back-button" onClick={goBack} type="button">
              Back
            </button>
          )}
          {navigation && currentNav.length === 1 && (
            <ContextAwareToggle
              buttonText={MOBILE_MENU.navHead}
              eventKey="NAV_KEY"
              faIcon={faShoppingCart}
            />
          )}
          <Accordion.Collapse eventKey="NAV_KEY">
            <ul className="mobile-menu__list">
              {currentNavItems &&
                currentNavItems?.map((item) => (
                  <MobileNavigationLink
                    href={item.url}
                    item={item}
                    key={item.id}
                    linkText={item.linkText}
                    onClick={() => openMenu(false)}
                    openSubMenu={openSubMenu}
                    selected={navSelection === item.id}
                  />
                ))}
            </ul>
          </Accordion.Collapse>
          {!isFindPage && (
            <button
              className="removeButton"
              onClick={() => handleShowSubModal(true)}
              type="button"
            >
              <MobileMenuSectionHead
                buttonText={MOBILE_MENU.subscribeHead}
                faIcon={faEnvelope}
                isCurrentEventKey={false}
                isExpander={false}
              />
            </button>
          )}
          <ContextAwareToggle
            buttonText={MOBILE_MENU.accountHead}
            eventKey="USER_KEY"
            faIcon={faUser}
          />
          <Accordion.Collapse eventKey="USER_KEY">
            <div>
              <ul className="mobile-menu__list">
                {userMenu.map((item) => (
                  <MobileNavigationLink
                    href={item.href}
                    item={item}
                    key={item.href}
                    linkText={item.text}
                    onClick={setCookie}
                  />
                ))}
                {userState && <HeaderLogout extraClass="navigation-link" />}
              </ul>
            </div>
          </Accordion.Collapse>
          <ContextAwareToggle
            buttonText={MOBILE_MENU.moreHead}
            eventKey="MORE_KEY"
            faIcon={faCog}
          />
          <Accordion.Collapse eventKey="MORE_KEY">
            <ul className="mobile-menu__list">
              {moreLinks.map((link) => {
                return (
                  <MobileNavigationLink
                    href={link.href}
                    key={link.href}
                    linkText={link.text}
                    selected={moreSelectionHref === link.href}
                  />
                );
              })}
              {!isFindPage && (
                <MobileNavigationLink
                  linkText={MOBILE_MENU.viewFooter}
                  onClick={showFooter}
                />
              )}
            </ul>
          </Accordion.Collapse>
        </Accordion>
      </div>
      <SubscribeModal
        isBadConnection={false}
        isLightBox
        list={locations}
        setShowModal={setShowSubModal}
        showCloseIcon
        showModal={showSubModal}
        showWhenAuthenticated
      />
      <style jsx>{`
        .mobile-menu {
          background-color: ${theme.colors.mobilemenubg};
        }
        .mobile-menu__list {
          border-bottom: 1px solid ${theme.colors.primary};
          margin-bottom: 0;
          max-height: calc(100vh - 286px);
          overflow-y: scroll;
          padding: 0;
        }

        .removeButton {
          border: 0;
          width: 100%;
          text-align: left;
          margin: 0;
          padding: 0;
        }

        .back-button {
          background: none;
          border: none;
          color: ${theme.colors.primary};
          padding: 5px 12px 5px 42px;
          text-transform: ${theme.text.navigation.transform};
          text-decoration: none;
          font-size: ${theme.text.navigation.size}px;
          white-space: nowrap;
          margin-top: 2%;
          margin-bottom: 2%;
        }
      `}</style>
    </>
  );
};

MobileMenu.propTypes = {
  isFindPage: PropTypes.bool,
  navigation: PropTypes.array,
  openMenu: PropTypes.func,
  path: PropTypes.string,
};

export default MobileMenu;
